import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import {
  IDataService,
  IDataResponse,
  IParsedDataResponse,
  IConstants,
  EConversionProfileFormats,
  IAvailableLanguage,
  LANGUAGE_CONSTANT,
  ICurrency,
} from './data-service.interface';
import { HttpClient, HttpContext } from '@angular/common/http';
import { IEnvironmentService } from '../environment/environment.interface';
import { map, shareReplay } from 'rxjs/operators';
import { TURN_OFF_INTERCEPTOR } from '../../interceptors/jwt.interceptor';
import {
  ADVERTISER_SYSTEM_ROLE_TRANSLATIONS,
  AFFILIATE_REQUESTS_STATUS_TRANSLATIONS,
  COUNTRIES_TRANSLATIONS,
  CRM_DOCUMENT_TYPE_TRANSLATIONS,
  CRM_QUESTION_TYPES_TRANSLATIONS,
  EARNING_STATUS_TRANSLATIONS,
  INVOICE_STATUSES_TRANSLATIONS,
  LANGUAGES_TRANSLATIONS,
  MONTHS_TRANSLATIONS,
  NEGATIVE_PERIOD_TRANSLATIONS,
  OFFER_PRIVACY_TRANSLATIONS,
  OFFER_STATUS_TRANSLATIONS,
  POSTBACK_STATUS_TRANSLATIONS,
  TRANSACTION_TYPE_TRANSLATIONS,
  CRM_PARTNER_TYPES,
  KYC_TYPES,
  KYC_APPROVE_MODES,
  OFFER_VERTICAL_TRANSLATIONS,
  TOKEN_TYPE_TRANSLATIONS,
  POSTBACK_CONTEXT_TRANSLATIONS,
  ACTIVITY_STATUS_BOOLEAN_TRANSLATIONS,
  COUNTRY_REGIONS_TRANSLATIONS,
} from './data-constants-translations';
import { BrowserWindowRef } from '../window/window.service';

@Injectable({
  providedIn: 'root',
})
export class DataService implements IDataService {
  private dataApi: string;
  private data: any;
  private constants$!: Observable<any>;

  constructor(
    @Inject('IEnvironmentService') private envService: IEnvironmentService,
    private http: HttpClient,
    @Inject(BrowserWindowRef) private window: BrowserWindowRef
  ) {
    this.dataApi = this.envService.getVar('services.api') as string;
  }

  setData(data: any): void {
    this.data = data;
  }

  parseData(origin: IDataResponse, constants: IConstants): IParsedDataResponse {
    let data: IParsedDataResponse = {
      version: origin.data.version || $localize`:@@common.local:local`,
      countries: this.translateConstants(constants.countries, COUNTRIES_TRANSLATIONS, 'name') || {},
      countriesWithoutAll: this.translateConstants(constants.countries, COUNTRIES_TRANSLATIONS, 'name') || {},
      currency_list: origin.data.currency_list || {},
      timezones: constants.timezones || {},
      advertisers: origin.data.advertisers || {},
      advertisers_active: origin.data.advertisers_active || {},
      affiliates: origin.data.affiliates || {},
      offers: {},
      goals: {},
      offer_goals: {},
      permissions: origin.data.permissions || [],
      roles: origin.data.roles || {},
      tracking_domains: origin.data.tracking_domains || {},
      users: origin.data.users || {},
      users_without_tech: {},
      months: this.translateConstants(constants.months, MONTHS_TRANSLATIONS) || {},
      invoice_currency_codes: origin.data.invoice_currency_codes || [],
      invoice_payment_systems: ['BTC', 'Wire Transfer', 'TRC20', 'ERC20', 'Neteller', 'Skrill'],
      payout_type: Object.entries(origin?.data?.payout_models || {}).reduce((acc, [key, value]) => {
        acc[key] = value.name;
        return acc;
      }, {}),
      revenue_type: Object.entries(origin?.data?.revenue_models || {}).reduce((acc, [key, value]) => {
        acc[key] = value.name;
        return acc;
      }, {}),
      referral_payout_type: Object.entries(origin?.data?.referral_payout_models || {}).reduce((acc, [key, value]) => {
        acc[key] = value.name;
        return acc;
      }, {}),
      terms: origin.data.terms || {},
      user_status: {
        0: $localize`:@@common.pending:Pending`,
        1: $localize`:@@common.active:Active`,
        2: $localize`:@@common.blocked:Blocked`,
        3: $localize`:@@common.paused:Paused`,
      },
      advertiser_status: {
        0: $localize`:@@common.pending:Pending`,
        1: $localize`:@@common.active:Active`,
        2: $localize`:@@common.blocked:Blocked`,
      },
      conversion_tracking_type: {
        1: $localize`:@@common.postback_with_click_id:Postback with Click ID`,
        2: $localize`:@@common.iframe_pixel:iFrame pixel`,
      },
      affiliate_tracker_type: {
        0: $localize`:@@common.postback:Postback`,
        1: $localize`:@@common.pixel:Pixel`,
      },
      goal_types: origin.data.goal_types.reduce((acc, { id, name }) => {
        acc[id] = name;
        return acc;
      }, {}),
      payout_labels: Object.entries(origin?.data?.payout_models || {}).reduce((acc, [key, value]) => {
        acc[key] = {
          hint: value.hint,
          placeholder: value.placeholder,
        };
        return acc;
      }, {}),
      revenue_labels: Object.entries(origin?.data?.revenue_models || {}).reduce((acc, [key, value]) => {
        acc[key] = {
          hint: value.hint,
          placeholder: value.placeholder,
        };
        return acc;
      }, {}),
      referral_payout_labels: Object.entries(origin?.data?.referral_payout_models || {}).reduce((acc, [key, value]) => {
        acc[key] = {
          hint: value.hint,
          placeholder: value.placeholder,
        };
        return acc;
      }, {}),
      referral_payout_models_labels: Object.entries(origin?.data?.referral_payout_models || {}).reduce((acc, [key, value]) => {
        acc[key] = value.name;
        return acc;
      }, {}),
      business_model_formats: origin?.data?.business_model_formats || {},
      languages: this.translateConstants(constants.languages, LANGUAGES_TRANSLATIONS, 'name') || {},
      terms_format: origin?.data?.terms_format || {},
      crm_document_types: this.translateConstants(origin?.data?.crm_document_types, CRM_DOCUMENT_TYPE_TRANSLATIONS) || {},
      crm_question_types: this.translateConstants(origin?.data?.crm_question_types, CRM_QUESTION_TYPES_TRANSLATIONS) || {},
      crm_partner_types: this.translateConstants(origin?.data?.crm_partner_types, CRM_PARTNER_TYPES) || {},
      affiliates_active: origin?.data?.affiliates_active || {},
      advertiser_providers: origin?.data?.advertiser_providers || {},
      affiliate_groups: origin?.data?.affiliate_groups || {},
      oauth: origin?.data?.oauth || {
        slack: {
          client_id: '',
          redirect_uri: '',
        },
      },
      affiliate_managers: origin?.data?.affiliate_managers || {},
      affiliate_trackers_placeholder: origin?.data?.affiliate_trackers_placeholder || [],
      offer_url_placeholders: origin?.data?.offer_url_placeholders || [],
      advertiser_system_roles: this.translateConstants(origin?.data?.advertiser_system_roles, ADVERTISER_SYSTEM_ROLE_TRANSLATIONS) || {},
      advertiser_brands: origin?.data?.advertiser_brands || {},
      advertiser_brands_map: origin?.data?.advertiser_brands_map || {},
      affiliate_manager_affiliates: origin?.data?.available_affiliates || {},
      agreement_status: origin?.data?.agreement_status || {},
      available_offer_source: origin?.data?.available_offer_source || {},
      available_offer_category: origin?.data?.available_offer_category || {},
      offer_redirect_types: origin?.data?.offer_redirect_types || {},
      postback_status: this.translateConstants(origin?.data?.postback_status, POSTBACK_STATUS_TRANSLATIONS) || {},
      offer_privacy: this.translateConstants(origin?.data.offer_privacy, OFFER_PRIVACY_TRANSLATIONS) || {},
      offer_status: this.translateConstants(origin?.data?.offer_status, OFFER_STATUS_TRANSLATIONS) || {},
      offer_urls_by_offer: {},
      offer_urls: origin?.data?.offer_urls || {},
      drill_down_menu: origin?.data?.drill_down_menu || [],
      baseline_fields: origin.data.baseline_fields || {},
      offer_urls_free_parameters: origin?.data?.offer_urls_free_parameters || {},
      default_currency: origin?.data?.default_currency || {},
      treeMoneyFormat: {
        money: 'Money',
        percent: 'Percent',
        default: 'Default',
      },
      baseline_types: origin?.data?.baseline_types || {},
      table_views: origin?.data.table_views || [],
      kyc_states: origin.data.kyc_states || {},
      kyc_types: this.translateConstants(origin.data.kyc_types, KYC_TYPES) || {},
      kyc_approve_modes: this.translateConstants(origin.data.kyc_approve_modes, KYC_APPROVE_MODES) || {},
      schemas: origin.data.schemas || {},
      advertiser_integrations: origin.data.integrations.filter((item) => item.type === 'pp-advertiser-provider') || [],
      public_ip: origin.data.public_ip || '',
      transaction_types: this.translateConstants(origin.data.transaction_types, TRANSACTION_TYPE_TRANSLATIONS) || {},
      no_negative_periods: this.translateConstants(origin.data.no_negative_periods, NEGATIVE_PERIOD_TRANSLATIONS),
      hoursFromData: constants.hoursFromData,
      hoursToData: constants.hoursToData,
      dayOfWeek: constants.dayOfWeek,
      exclude_clicks_fields: origin.data.exclude_clicks_fields || {},
      exclude_conversions_fields: origin.data.exclude_conversions_fields || {},
      aff_crm_exclude_clicks_fields: origin.data.aff_crm_exclude_clicks_fields || {},
      aff_crm_exclude_offers_fields: origin.data.aff_crm_exclude_offers_fields || {},
      aff_crm_exclude_conversions_fields: origin.data.aff_crm_exclude_conversions_fields || {},
      aff_crm_include_conversions_fields: origin.data.aff_crm_include_conversions_fields || {},
      payment_statuses: {
        draft: $localize`:@@common.draft:Draft`,
        declined: $localize`:@@common.declined:Declined`,
        approved: $localize`:@@common.approved:Approved`,
        sent: $localize`:@@common.sent:Sent`,
        arrived: $localize`:@@common.arrived:Arrived`,
        paid: $localize`:@@common.paid:Paid`,
      },
      earning_statuses: {
        draft: $localize`:@@common.draft:Draft`,
        applied: $localize`:@@common.applied:Applied`,
        paid: $localize`:@@common.paid:Paid`,
      },
      default_payment_systems_list: origin.data.default_payment_systems_list || {},
      invoice_statuses: {
        draft: $localize`:@@common.draft:Draft`,
        declined: $localize`:@@common.declined:Declined`,
        approved: $localize`:@@common.approved:Approved`,
        paid: $localize`:@@common.paid:Paid`,
        pending: $localize`:@@common.pending:Pending`,
      },
      payment_systems: [],
      payment_systems_list: origin.data.payment_systems_list || [],
      postback_context: this.translateConstants(origin.data.postback_context, POSTBACK_CONTEXT_TRANSLATIONS),
      conversion_profile_fields: origin.data.conversion_profile_fields || {},
      conversionProfileFormats: {
        [EConversionProfileFormats.TEXT]: $localize`:@@common.text:Text`,
        [EConversionProfileFormats.NUMBER]: $localize`:@@common.number:Number`,
        [EConversionProfileFormats.EMAIL]: $localize`:@@common.email:Email`,
        [EConversionProfileFormats.DATE]: $localize`:@@common.date:Date`,
        [EConversionProfileFormats.MONEY]: $localize`:@@common.money:Money`,
        [EConversionProfileFormats.BOOLEAN]: $localize`:@@common.boolean:Boolean`,
        [EConversionProfileFormats.MONEY_WITH_CURRENCY]: $localize`:@@common.money_with_currency:Money With Currency`,
      },
      description_presets: origin.data.description_presets || [],
      referral_payout_models: origin.data.referral_payout_models,
      referral_programs: origin.data.referral_programs || [],
      default_privacy_options: {
        1: $localize`:@@common.private:Private`,
        0: $localize`:@@common.public:Public`,
      },
      offer_requests_statuses: this.translateConstants(origin.data.offer_requests_statuses, AFFILIATE_REQUESTS_STATUS_TRANSLATIONS),
      payout_models: origin.data.payout_models ?? {},
      revenue_models: origin.data.revenue_models ?? {},
      default_currencies: origin.data.default_currencies ?? {},
      currency_provider: origin.data.currency_provider,
      currency_integrations: origin.data.currency_integrations ?? [],
      conversion_profile_fields_placeholders: origin.data.conversion_profile_fields_placeholders ?? [],
      earning_total_formula_placeholders: origin.data.earning_total_formula_placeholders ?? [],
      affiliate_clusters: origin.data.affiliate_clusters ?? {},
      available_languages: constants.availableLanguages,
      current_language: this.getCurrentLanguage(constants.availableLanguages),
      pending_unassign_affiliates: origin.data.pending_unassign_affiliates ?? {},
      days: {
        Monday: $localize`:@@dates.monday:Monday`,
        Tuesday: $localize`:@@dates.tuesday:Tuesday`,
        Wednesday: $localize`:@@dates.wednesday:Wednesday`,
        Thursday: $localize`:@@dates.thursday:Thursday`,
        Friday: $localize`:@@dates.friday:Friday`,
        Saturday: $localize`:@@dates.saturday:Saturday`,
        Sunday: $localize`:@@dates.sunday:Sunday`,
      },
      finance_earning_statuses: this.translateConstants(origin.data.finance_earning_statuses, EARNING_STATUS_TRANSLATIONS),
      finance_invoice_statuses: this.translateConstants(origin.data.finance_invoice_statuses, INVOICE_STATUSES_TRANSLATIONS),
      isUnpaid: origin.data.isUnpaid ?? false,
      verticals: this.translateConstants(origin.data.verticals, OFFER_VERTICAL_TRANSLATIONS),
      default_currencies_options: this.getCurrencyOptions(
        Object.values(origin?.data?.default_currencies) || [],
        origin?.data?.currency_list
      ),
      offer_currencies_options: this.getCurrencyOptions(
        origin?.data?.available_offer_currencies?.filter((code) => Boolean(code)) || [],
        origin?.data?.currency_list
      ),
      available_offer_currencies: origin.data.available_offer_currencies ?? [],
      tokens_type: this.translateConstants(origin.data.tokens_type, TOKEN_TYPE_TRANSLATIONS),
      activity_status_boolean: this.translateConstants(origin.data.activity_status_boolean, ACTIVITY_STATUS_BOOLEAN_TRANSLATIONS),
      conversion_profile_fields_for_formula_placeholders: origin.data.conversion_profile_fields_for_formula_placeholders || [],
      offer_advertisers: {},
      languages_with_all: {},
    };
    delete data.countriesWithoutAll['**'];
    Object.keys(data.countries).forEach((countryKey) => {
      if (data.countries[countryKey].regions) {
        data.countries[countryKey].regions = this.translateConstants(data.countries[countryKey].regions, COUNTRY_REGIONS_TRANSLATIONS);
      }
    });

    data.payment_systems =
      origin.data.payment_systems?.map((paymentSystem) => {
        const codes = this.prepareCurrencies(paymentSystem.currencies, origin?.data?.currency_list || {});

        return { name: paymentSystem.name, codes };
      }) ?? [];

    for (const userId of Object.keys(origin.data.users)) {
      const userName = origin.data.users[userId];
      if (!userName.includes('System Tech')) {
        data.users_without_tech[userId] = userName;
      }
    }

    for (const offerId of Object.keys(origin.data.offers)) {
      const offer = origin.data.offers[offerId];
      data.offers[offerId] = offer.name;
      data.offer_goals[offerId] = {};
      data.offer_urls_by_offer[offerId] = {};
      data.offer_advertisers[offerId] = origin.data.offers[offerId].advertiser_id;

      for (const goalId of Object.keys(offer.goals)) {
        data.goals[goalId] = offer.goals[goalId];
        data.offer_goals[offerId][goalId] = offer.goals[goalId];
      }
      for (const offerUrlId in offer.offer_urls) {
        if (Object.prototype.hasOwnProperty.call(offer.offer_urls, offerUrlId)) {
          data.offer_urls_by_offer[offerId][offerUrlId] = offer.offer_urls[offerUrlId];
        }
      }
    }

    data.languages_with_all = {
      '**': {
        name: $localize`:@@common.all_languages:All Languages`,
        code: '**',
        img: '',
      },
      ...data.languages,
    };

    data = {
      ...origin.data,
      ...data,
    };

    return data;
  }

  getDataFromServer(): Observable<any> {
    if (!this.constants$) {
      this.constants$ = this.http
        .get(`/assets/other/data-constants.json`, { context: new HttpContext().set(TURN_OFF_INTERCEPTOR, true) })
        .pipe(shareReplay());
    }

    return forkJoin([this.http.get(`${this.dataApi}/data`), this.constants$]).pipe(
      map(([data, constants]: [any, any]) => {
        return this.parseData(data, constants);
      })
    );
  }

  getData(): any {
    return this.data;
  }

  private prepareCurrencies(currencies: string[], currencyList: ICurrency): Record<string, any> {
    return currencies.reduce((list, currency) => {
      list[currency] = currencyList[currency];
      return list;
    }, {});
  }

  private translateConstants<T>(constants: T, translations: Record<keyof T, string>, field?: string): T {
    Object.keys(constants).map((key) => {
      if (field) {
        constants[key][field] = translations[key];
      } else {
        constants[key] = translations[key] ?? constants[key];
      }
    });
    return constants;
  }

  private getCurrentLanguage(availableLanguages: Record<string, IAvailableLanguage>): string {
    const currentLanguage = this.window.nativeWindow.location.pathname.split('/')[1] ?? LANGUAGE_CONSTANT.EN;
    return availableLanguages[currentLanguage] ? currentLanguage : LANGUAGE_CONSTANT.EN;
  }

  private getCurrencyOptions(codes: string[], currencyList: ICurrency): ICurrency {
    return codes.reduce((options, code) => {
      options[code] = currencyList[code];
      return options;
    }, {});
  }
}
